import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Eye, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useConfigByGrupo, useUpdateConfig, previewPlantillaFactura,
} from "@/features/config/api";
import type { ApiError } from "@/lib/api";
import { openBlobInNewTab } from "@/lib/pdf";

const VARIABLES_DISPONIBLES: { key: string; desc: string }[] = [
  { key: "{{ isp.nombre_legal }}", desc: "Razón social del ISP" },
  { key: "{{ isp.nombre_comercial }}", desc: "Nombre comercial" },
  { key: "{{ isp.rfc }}", desc: "RFC del ISP" },
  { key: "{{ isp.domicilio_fiscal }}", desc: "Domicilio fiscal" },
  { key: "{{ isp.telefono }}", desc: "Teléfono" },
  { key: "{{ isp.email }}", desc: "Email" },
  { key: "{{ cliente.razon_social }}", desc: "Razón social del cliente" },
  { key: "{{ cliente.nombre_comercial }}", desc: "Nombre comercial del cliente" },
  { key: "{{ cliente.rfc }}", desc: "RFC del cliente" },
  { key: "{{ cliente.codigo }}", desc: "Código del cliente" },
  { key: "{{ contrato.codigo }}", desc: "Código del contrato (si aplica)" },
  { key: "{{ factura.folio }}", desc: "Folio de la factura" },
  { key: "{{ factura.fecha_emision }}", desc: "Fecha de emisión" },
  { key: "{{ factura.fecha_vencimiento }}", desc: "Fecha de vencimiento" },
  { key: "{{ factura.fecha_pago }}", desc: "Fecha de pago (si pagada)" },
  { key: "{{ factura.periodo_inicio }}", desc: "Período inicio" },
  { key: "{{ factura.periodo_fin }}", desc: "Período fin" },
  { key: "{{ factura.subtotal }}", desc: "Subtotal" },
  { key: "{{ factura.descuento_pct }}", desc: "% de descuento" },
  { key: "{{ factura.descuento_monto }}", desc: "Monto de descuento" },
  { key: "{{ factura.total }}", desc: "Total" },
  { key: "{{ factura.saldo }}", desc: "Saldo pendiente" },
  { key: "{{ factura.moneda }}", desc: "Moneda (MXN, USD)" },
  { key: "{{ factura.estatus_label }}", desc: "Estatus legible" },
  { key: "{{ factura.motivo_anulacion }}", desc: "Motivo si está anulada" },
  { key: "{{ factura.notas }}", desc: "Notas al cliente" },
  { key: "{% for item in items %}", desc: "Inicio loop de conceptos" },
  { key: "{{ item.concepto }}", desc: "Concepto del ítem" },
  { key: "{{ item.cantidad }}", desc: "Cantidad" },
  { key: "{{ item.precio_unitario }}", desc: "Precio unitario" },
  { key: "{{ item.importe }}", desc: "Importe" },
  { key: "{% endfor %}", desc: "Fin loop" },
  { key: "{% for p in pagos %}", desc: "Inicio loop de pagos aplicados" },
  { key: "{{ p.folio }}", desc: "Folio del pago" },
  { key: "{{ p.fecha }}", desc: "Fecha del pago" },
  { key: "{{ p.metodo_label }}", desc: "Método de pago" },
  { key: "{{ p.monto }}", desc: "Monto del pago" },
  { key: "{{ fecha_hoy }}", desc: "Fecha actual (DD/MM/YYYY)" },
];

export function PlantillaFacturaSection() {
  const { data: items, isLoading } = useConfigByGrupo("factura");
  const updateMut = useUpdateConfig();
  const [plantilla, setPlantilla] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (items) {
      const item = items.find((i) => i.key === "factura.plantilla_html");
      setPlantilla(typeof item?.value === "string" ? item.value : "");
      setDirty(false);
    }
  }, [items]);

  async function handleSave() {
    try {
      await updateMut.mutateAsync({
        key: "factura.plantilla_html",
        value: plantilla,
        value_type: "string",
        grupo: "factura",
      });
      toast.success("Plantilla de factura guardada");
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", {
        description: (e as ApiError).message,
      });
    }
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const blob = await previewPlantillaFactura(plantilla);
      openBlobInNewTab(blob);
    } catch (e) {
      toast.error("Error al generar preview", {
        description: (e as Error).message,
      });
    } finally {
      setPreviewing(false);
    }
  }

  function insertVariable(v: string) {
    setPlantilla((prev) => prev + v);
    setDirty(true);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          La plantilla usa sintaxis <b>Jinja2</b> sobre HTML. Las variables entre{" "}
          <code>{"{{ ... }}"}</code> se reemplazan con los datos reales al generar
          el PDF de la factura. El loop <code>{"{% for item in items %}"}</code>{" "}
          itera sobre los conceptos; <code>{"{% for p in pagos %}"}</code> sobre los
          pagos aplicados. El <b>Preview</b> genera un PDF con datos de ejemplo.
          <br />
          <b>Nota</b>: este PDF es un recibo interno — NO sustituye el CFDI timbrado
          del SAT (emisión CFDI real llega en v1.1 con flag de factura.com).
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_260px]">
        <Textarea
          value={plantilla}
          onChange={(e) => { setPlantilla(e.target.value); setDirty(true); }}
          rows={28}
          className="font-mono text-[11px]"
          spellCheck={false}
        />

        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold">Variables disponibles</h3>
          <p className="text-[10px] text-muted-foreground">
            Clic para insertar al final del editor.
          </p>
          <div className="flex max-h-[620px] flex-col gap-0.5 overflow-y-auto rounded border p-1">
            {VARIABLES_DISPONIBLES.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => insertVariable(v.key)}
                className="flex flex-col items-start gap-0 rounded px-1.5 py-1 text-left text-[10px] hover:bg-accent"
              >
                <code className="font-mono">{v.key}</code>
                <span className="text-muted-foreground">{v.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs text-muted-foreground">Cambios sin guardar</span>
        )}
        <Button size="sm" variant="outline" onClick={handlePreview} disabled={previewing}>
          <Eye size={12} />
          {previewing ? "Generando…" : "Ver preview PDF"}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
          <Save size={12} />
          {updateMut.isPending ? "Guardando…" : "Guardar plantilla"}
        </Button>
      </div>
    </div>
  );
}
